// Runtime catalog loader. English is statically bundled (the default, and
// the fallback while a non-default catalog streams in); es/fr and any
// future locale are code-split and fetched on demand.
//
// Glob-driven so a new language needs only a `apps/web/messages/<code>/`
// folder — no per-locale aggregator module to keep in sync. The namespace
// key is the capitalized file stem (`common.json` → `Common`,
// `messages.json` → `Messages`), matching `./en.ts`.
//
// English is also the per-key content fallback: a non-default catalog is
// deep-merged over English, so any key a locale hasn't translated resolves
// to the English string — parsed and interpolated as ICU, not shown as a
// raw key path. This is the documented use-intl fallback-locale pattern and
// makes a stored es/fr member render English (never `Namespace.key`) on any
// surface that outpaces its translations. A key missing from *every* locale
// is a genuine bug and still surfaces via use-intl's default error path.
import { DEFAULT_LOCALE, type UserLocale } from '@leanin/shared';
import enMessages from './en';

export type Catalog = typeof enMessages;

const namespaceModules = import.meta.glob<{ default: Record<string, unknown> }>(
  '../../messages/*/*.json',
);

function namespaceKey(fileStem: string): string {
  return fileStem.charAt(0).toUpperCase() + fileStem.slice(1);
}

function isMessageTree(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Recursively overlay `override` onto `base`. Nested namespaces merge key by
// key; a leaf present in `override` wins, a leaf only in `base` (a
// not-yet-translated key) is retained. Catalog values are only ever ICU
// strings or nested objects — no arrays — so a value that isn't a tree on
// both sides is a leaf the override replaces.
export function deepMergeCatalog(
  base: Record<string, unknown>,
  override: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...base };
  for (const [key, overrideValue] of Object.entries(override)) {
    const baseValue = merged[key];
    merged[key] =
      isMessageTree(baseValue) && isMessageTree(overrideValue)
        ? deepMergeCatalog(baseValue, overrideValue)
        : overrideValue;
  }
  return merged;
}

export async function loadCatalog(locale: UserLocale): Promise<Catalog> {
  if (locale === DEFAULT_LOCALE) return enMessages;
  const marker = `/messages/${locale}/`;
  const entries = await Promise.all(
    Object.entries(namespaceModules)
      .filter(([path]) => path.includes(marker))
      .map(async ([path, load]) => {
        const stem = path.slice(path.lastIndexOf('/') + 1).replace(/\.json$/, '');
        const mod = await load();
        return [namespaceKey(stem), mod.default] as const;
      }),
  );
  const localeCatalog = Object.fromEntries(entries) as Record<string, unknown>;
  return deepMergeCatalog(enMessages, localeCatalog) as Catalog;
}
